import { useEffect, useMemo, useRef, useState } from 'react'
import { PointsBadge } from '@/components/PointsBadge'
import type {
  PouleAppRound,
  PouleDashboardBootstrapResponse,
  PouleRoundRankingEntry,
} from '@/features/poules/api'
import { usePouleAppRounds, usePouleRoundRanking } from '@/features/poules/api'
import { ModalCloseButton } from '@/components/ModalCloseButton'
import { getLocale, toIntlLocale } from '@/config/locale'
import { useTranslation } from '@/i18n'

export function PouleDashboardRoundRankingCard({
  pouleId,
  dashboard,
}: {
  pouleId: number
  dashboard: PouleDashboardBootstrapResponse['dashboard']
}) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const round = dashboard?.round?.round
  const points = typeof dashboard?.round?.points === 'number' ? dashboard.round.points : 0

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="block h-full w-full rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] p-5 text-left transition hover:border-surface-600 hover:bg-[#151a1c] focus-visible:border-surface-500 focus-visible:bg-[#151a1c] focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d1113]"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
          {round?.sequence ? t('Speelronde {number}', { number: round.sequence }) : t('Speelronde')}
        </p>
        <div className="mt-3 flex items-end gap-2">
          <span className="text-3xl font-black tracking-tight text-white">{points}</span>
          <span className="pb-1 text-sm font-medium text-surface-400">{t('punten')}</span>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 text-xs font-semibold text-surface-400">
          <span>{t('Behaald')}</span>
          <span aria-hidden="true" className="text-lg">→</span>
        </div>
      </button>

      {isOpen ? (
        <PouleRoundRankingOverlay
          pouleId={pouleId}
          currentRoundId={round?.id}
          currentRoundName={round?.name}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </>
  )
}

function PouleRoundRankingOverlay({
  pouleId,
  currentRoundId,
  currentRoundName,
  onClose,
}: {
  pouleId: number
  currentRoundId?: number | undefined
  currentRoundName?: string | null | undefined
  onClose: () => void
}) {
  const { t } = useTranslation()
  const roundsQuery = usePouleAppRounds(pouleId, true)
  const rounds = roundsQuery.data ?? []
  const focusableRounds = useMemo(() => rounds.filter(isRoundFocusable), [rounds])
  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(null)
  const selectedRoundTabRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  useEffect(() => {
    const nextRound = chooseInitialRound(rounds, currentRoundId)
    if (selectedRoundId == null) {
      setSelectedRoundId(nextRound?.id ?? null)
      return
    }

    if (!focusableRounds.some((round) => round.id === selectedRoundId)) {
      setSelectedRoundId(nextRound?.id ?? null)
    }
  }, [currentRoundId, rounds, focusableRounds, selectedRoundId])

  useEffect(() => {
    if (selectedRoundId == null || !selectedRoundTabRef.current) return
    selectedRoundTabRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [selectedRoundId, rounds.length])

  const selectedRound =
    rounds.find((round) => round.id === selectedRoundId) ?? chooseInitialRound(rounds, currentRoundId)
  const rankingQuery = usePouleRoundRanking(
    pouleId,
    selectedRound?.id ?? null,
    selectedRound != null,
  )
  const ranking = rankingQuery.data ?? []

  return (
    <>
      <div className="animate-drawer-backdrop-in fixed inset-0 z-[72] bg-black/88 backdrop-blur-sm" />
      <div className="fixed inset-0 z-[73] overflow-y-auto">
        <div className="mx-auto flex min-h-full w-full max-w-5xl items-start justify-center p-4 pt-5 lg:items-center lg:p-8">
          <div className="animate-modal-panel-in relative w-full overflow-hidden rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113] shadow-[0_24px_72px_rgba(0,0,0,0.5)]">
            <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />

            <div className="relative">
              <div className="flex items-start justify-between gap-4 border-b border-[#202427] px-5 py-4 sm:px-6">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-surface-500">
                    {t('Dashboard ranking')}
                  </p>
                  <h2 className="mt-2 text-xl font-black tracking-tight text-white sm:text-2xl">
                    {t('Stand per speelronde')}
                  </h2>
                  <p className="mt-1.5 text-sm text-surface-400">
                    {currentRoundName?.trim()
                      ? t('Start in {round} of wissel naar een andere ronde met scores.', { round: currentRoundName.trim() })
                      : t('Kies een speelronde met scores en bekijk direct de stand.')}
                  </p>
                </div>
                <ModalCloseButton onClick={onClose} />
              </div>

              <div className="px-5 py-4 sm:px-6">
                {roundsQuery.isLoading && rounds.length === 0 ? (
                  <StateCard
                    title={t('Speelrondes laden…')}
                    body={t('We halen eerst alle beschikbare speelrondes voor deze poule op.')}
                  />
                ) : null}

                {roundsQuery.isError && rounds.length === 0 ? (
                  <StateCard
                    tone="error"
                    title={t('Speelrondes niet geladen')}
                    body={roundsQuery.error?.message || t('De speelrondes konden niet worden geladen.')}
                    actionLabel={t('Opnieuw proberen')}
                    onAction={() => {
                      void roundsQuery.refetch()
                    }}
                  />
                ) : null}

                {!roundsQuery.isLoading && !roundsQuery.isError && rounds.length === 0 ? (
                  <StateCard
                    title={t('Nog geen speelrondes')}
                    body={t('Deze poule heeft nog geen speelrondes om te tonen.')}
                  />
                ) : null}

                {rounds.length > 0 && focusableRounds.length === 0 ? (
                  <StateCard
                    title={t('Nog geen rondes met scores')}
                    body={t('De speelrondes zijn al bekend, maar er zijn nog geen scores verwerkt om een stand te tonen.')}
                  />
                ) : null}

                {rounds.length > 0 && focusableRounds.length > 0 ? (
                  <>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {rounds.map((round) => {
                        const active = round.id === selectedRound?.id
                            const selectable = isRoundFocusable(round)

                        return (
                          <button
                            key={round.id}
                            type="button"
                            ref={active ? selectedRoundTabRef : undefined}
                            disabled={!selectable}
                            onClick={() => setSelectedRoundId(round.id)}
                            className={[
                              'min-w-[11rem] rounded-[1rem] border px-3 py-3 text-left transition',
                              active
                                ? 'border-[#315c6b] bg-[#10191c] text-white shadow-[0_10px_28px_rgba(74,111,124,0.1)]'
                                : 'border-[#202427] bg-[#0d1113] text-surface-300 hover:border-[#3a464b] hover:bg-[#0e1520]',
                              !selectable ? 'cursor-not-allowed opacity-45 hover:border-[#202427] hover:bg-[#0d1113]' : '',
                            ].join(' ')}
                          >
                            <p className="text-sm font-black">
                              {getRoundName(t, round.name, round.sequence, round.id)}
                            </p>
                            <p className="mt-1 text-xs text-surface-400">
                              {getRoundMetaLabel(t, round)}
                            </p>
                          </button>
                        )
                      })}
                    </div>

                    {selectedRound ? (
                      <section className="mt-4 rounded-[1.2rem] border border-[#202427] bg-[#0d1011] p-4">
                        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-surface-500">
                              {t('Geselecteerde ronde')}
                            </p>
                            <h3 className="mt-2 text-lg font-black text-white">
                              {getRoundName(t, selectedRound.name, selectedRound.sequence, selectedRound.id)}
                            </h3>
                            <p className="mt-1 text-sm text-surface-400">
                              {getRoundWindowLabel(t, selectedRound)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <InfoPill label={getRoundStatusLabel(t, selectedRound.status)} />
                            {selectedRound.is_current ? <InfoPill label={t('Huidige ronde')} tone="accent" /> : null}
                          </div>
                        </div>
                      </section>
                    ) : null}

                    <section className="mt-4 rounded-[1.2rem] border border-[#202427] bg-[#0d1011] p-3 sm:p-4">
                      {rankingQuery.isLoading && ranking.length === 0 ? (
                        <StateCard
                          title={t('Ranking laden…')}
                          body={t('We halen de stand van deze speelronde op.')}
                        />
                      ) : null}

                      {rankingQuery.isError && ranking.length === 0 ? (
                        <StateCard
                          tone="error"
                          title={t('Ranking niet geladen')}
                          body={
                            rankingQuery.error?.message ||
                            t('De speelrondestand kon niet worden geladen.')
                          }
                          actionLabel={t('Opnieuw proberen')}
                          onAction={() => {
                            void rankingQuery.refetch()
                          }}
                        />
                      ) : null}

                      {!rankingQuery.isLoading && !rankingQuery.isError && ranking.length === 0 ? (
                        <StateCard
                          title={t('Nog geen ranking')}
                          body={t('Voor deze speelronde is nog geen stand beschikbaar.')}
                        />
                      ) : null}

                      {ranking.length > 0 ? (
                        <>
                          {rankingQuery.isFetching ? (
                            <p className="px-2 pb-3 text-right text-xs uppercase tracking-[0.18em] text-surface-600">
                              Ranking wordt ververst…
                            </p>
                          ) : null}

                          <div className="space-y-2.5">
                            {ranking.map((entry) => (
                              <RankingRow key={`${entry.user_id ?? entry.position}-${entry.position}`} entry={entry} />
                            ))}
                          </div>
                        </>
                      ) : null}
                    </section>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function RankingRow({ entry }: { entry: PouleRoundRankingEntry }) {
  const { t } = useTranslation()
  return (
    <article
      className={[
        'flex items-center gap-3 rounded-[1rem] border px-3.5 py-3',
        entry.is_me
          ? 'border-[#315c6b] bg-[#10191c]'
          : 'border-[#202427] bg-[#0d1113]',
      ].join(' ')}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#151a1c] text-sm font-black text-white">
        {entry.position}
      </div>
      <Avatar name={entry.user_name} profileImage={entry.profile_image} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-white">{entry.user_name}</p>
          {entry.is_me ? <InfoPill label={t('Jij')} tone="accent" /> : null}
        </div>
        <p className="mt-1 text-xs text-surface-400">
          {entry.points === 1 ? '1 punt in deze ronde' : `${entry.points} punten in deze ronde`}
        </p>
      </div>
      <div className="shrink-0">
        <PointsBadge points={entry.points} />
      </div>
    </article>
  )
}

function StateCard({
  title,
  body,
  tone = 'neutral',
  actionLabel,
  onAction,
}: {
  title: string
  body: string
  tone?: 'neutral' | 'error'
  actionLabel?: string
  onAction?: () => void
}) {
  const className =
    tone === 'error'
      ? 'border-red-900/60 bg-red-950/25 text-red-100'
      : 'border-white/[0.06] bg-[#0d1113] text-surface-100'

  return (
    <section className={`rounded-[1.2rem] border p-5 ${className}`}>
      <h3 className="text-lg font-black tracking-tight text-inherit">{title}</h3>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-inherit/80">{body}</p>
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-4 rounded-full border border-current/20 px-4 py-2 text-sm font-semibold text-inherit transition hover:bg-white/5"
        >
          {actionLabel}
        </button>
      ) : null}
    </section>
  )
}

function InfoPill({
  label,
  tone = 'default',
}: {
  label: string
  tone?: 'default' | 'accent'
}) {
  return (
    <span
      className={[
        'rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em]',
        tone === 'accent'
          ? 'border border-[#203841] bg-[#10191c] text-[#93d9ff]'
          : 'border border-[#202427] bg-[#0d1113] text-surface-400',
      ].join(' ')}
    >
      {label}
    </span>
  )
}

function Avatar({
  name,
  profileImage,
}: {
  name: string
  profileImage?: string | null | undefined
}) {
  if (profileImage) {
    return (
      <img
        src={profileImage}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover"
      />
    )
  }

  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#151a1c] text-sm font-black uppercase text-surface-300">
      {getInitials(name)}
    </span>
  )
}

function chooseInitialRound(
  rounds: PouleAppRound[],
  currentRoundId?: number | undefined,
) {
  return (
    rounds.find((round) => round.id === currentRoundId && isRoundFocusable(round)) ??
    rounds.find((round) => isActiveRound(round) && isRoundFocusable(round)) ??
    [...rounds].reverse().find((round) => round.status === 'completed' && isSelectableRound(round)) ??
    rounds.find(isSelectableRound)
  )
}

function isSelectableRound(round: PouleAppRound) {
  return round.has_scores === true
}

function isRoundFocusable(round: PouleAppRound) {
  return isSelectableRound(round) || isActiveRound(round)
}

function isActiveRound(round: PouleAppRound) {
  const status = round.status?.trim().toLowerCase()
  return round.is_current === true || status === 'in_progress' || status === 'in progress' || status === 'active' || status === 'live' || status === 'current'
}

type Translate = (key: string, values?: Record<string, string | number>) => string

function getRoundName(
  t: Translate,
  name?: string | null | undefined,
  sequence?: number | null | undefined,
  id?: number | undefined,
) {
  const normalized = name?.trim()
  if (normalized) return normalized
  if (typeof sequence === 'number') return t('Speelronde {number}', { number: sequence })
  if (typeof id === 'number') return t('Ronde {number}', { number: id })
  return t('Onbekende ronde')
}

function getRoundMetaLabel(t: Translate, round: PouleAppRound) {
  if (round.is_current) return t('Huidige ronde')
  if (!isSelectableRound(round) && !isActiveRound(round)) return t('Nog geen scores')
  return getRoundStatusLabel(t, round.status)
}

function getRoundStatusLabel(t: Translate, status?: string | null | undefined) {
  switch (status?.trim().toLowerCase()) {
    case 'in_progress':
      return t('Bezig')
    case 'completed':
      return t('Afgerond')
    case 'scheduled':
      return t('Gepland')
    default:
      return status?.trim() || t('Beschikbaar')
  }
}

function getRoundWindowLabel(t: Translate, round: PouleAppRound) {
  const start = formatShortDate(round.starts_at)
  const end = formatShortDate(round.ends_at)

  if (start && end) return t('{start} t/m {end}', { start, end })
  if (start) return t('Start {date}', { date: start })
  if (end) return t('Einde {date}', { date: end })
  return t('Speelperiode nog niet beschikbaar.')
}

function formatShortDate(value?: string | null | undefined) {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined

  return new Intl.DateTimeFormat(toIntlLocale(getLocale()), {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

function getInitials(value: string) {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}
